import { useMemo, useState } from 'react';
import { Download, Loader2, ChevronLeft, ChevronRight, CheckCircle2, Info } from 'lucide-react';
import {
    useAuditLog,
    useExportAuditLog,
    useCreateAuditReview,
    useLatestAuditReview,
    type AuditLogFilter,
    type AuditResult,
    type LatestAuditReview,
} from '@/api/auditLog';
import { useUserDirectory } from '@/api/users';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Input, Textarea } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
    DialogFooter, DialogClose,
} from '@/components/ui/Dialog';
import { toIsoDate } from '@/lib/calendar';

/*
 * AuditLogPage — Phase 2.10 viewer.
 *
 * Forensic tool used by the Security Officer during the monthly
 * §164.308(a)(1)(ii)(D) review. The filter UI mirrors the saved searches in
 * docs/compliance/information-system-activity-review.md §2.1–§2.5 so a
 * reviewer can step through the prescribed patterns without reconstructing
 * filter strings from memory.
 *
 * - The table is read-only; every append goes through the backend's
 *   writeAudit path (append-only, hash-chained).
 * - Export and Mark-reviewed are themselves audited server-side — the viewer
 *   explicitly does NOT hide them from the list it just loaded, because the
 *   reviewer may want to see the "I just marked reviewed" row appear on the
 *   next refresh.
 * - PHI exposure: the subject column can contain client names (for detail
 *   reads like "Client viewed" → "Jane Doe"). Reviewers already hold
 *   view_audit_log, and the server has audited the read; the page does not
 *   need to re-redact.
 */

const PAGE_SIZE = 100;

interface Preset {
    id: string;
    label: string;
    description: string;
    disabled?: boolean;
    build: () => AuditLogFilter;
}

const STATIC_PRESETS: Preset[] = [
    {
        id: 'failed-logins',
        label: 'Failed logins',
        description: 'Credential-stuffing / brute-force / MFA struggles.',
        build: () => ({ category: 'Auth', action: 'Login failed' }),
    },
    {
        id: 'off-hours-phi',
        label: 'PHI access (all read actions)',
        description: 'Combine with a date window to scan for off-hours access.',
        build: () => ({ category: 'Clinical', action: 'viewed' }),
    },
    {
        id: 'listings',
        label: 'List reads (bulk enumeration)',
        description: 'Anything ending in "listed" — subject includes row counts for a quick scan of high-volume pulls.',
        build: () => ({ action: 'listed' }),
    },
    {
        id: 'auth-admin',
        label: 'Auth + admin events',
        description: 'Session revocation, logout-everywhere, role/permission changes.',
        build: () => ({ category: 'Auth' }),
    },
    {
        id: 'failures',
        label: 'Failures only',
        description: 'Any row with a Failure result — access denied, permission-denied, MFA failures.',
        build: () => ({ result: 'Failure' as AuditResult }),
    },
];

function isoDate(iso: string): string {
    // reviewedAt is an ISO timestamp; audit-log date filters are YYYY-MM-DD.
    return iso.slice(0, 10);
}

/**
 * The "Since last review" chip anchors to the most recent review's date.
 * Disabled (with an explanatory tooltip) when no review has been logged yet.
 */
function sinceLastReviewPreset(latest: LatestAuditReview | null | undefined): Preset {
    if (!latest) {
        return {
            id: 'since-last-review',
            label: 'Since last review',
            description: 'No prior review found — log a first one to anchor this filter.',
            disabled: true,
            build: () => ({}),
        };
    }
    const from = isoDate(latest.reviewedAt);
    return {
        id: 'since-last-review',
        label: `Since last review (${from})`,
        description: `Filter from ${from} onward — rows posted since the most recent audit-log review.`,
        build: () => ({ dateFrom: from }),
    };
}

const RESULT_OPTIONS: Array<{ value: '' | AuditResult; label: string }> = [
    { value: '', label: 'All results' },
    { value: 'Success', label: 'Success' },
    { value: 'Failure', label: 'Failure' },
    { value: 'Other', label: 'Other' },
];

// Canonical category set written by `writeAudit` across the codebase.
// Keeping this as a closed enum (rather than an ad-hoc string) because every
// server-side audit site classifies into exactly one of these — leaving
// Category as a free-text filter let reviewers type a near-miss like
// "Clinicals" and get zero rows back.
const CATEGORY_OPTIONS: Array<{ value: string; label: string }> = [
    { value: '', label: 'All categories' },
    { value: 'Clinical', label: 'Clinical' },
    { value: 'Billing', label: 'Billing' },
    { value: 'Auth', label: 'Auth' },
    { value: 'Admin', label: 'Admin' },
    { value: 'Audit', label: 'Audit' },
];

function fmtTs(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
    });
}

function resultTone(result: string): string {
    const r = result.toLowerCase();
    if (r.startsWith('fail')) return 'text-error font-medium';
    if (r.startsWith('success')) return 'text-text-subtle';
    return 'text-warning-text';
}

export function AuditLogPage() {
    // `draft` is bound to the form inputs; `applied` is what the query uses.
    // Keeping them separate avoids a fetch per keystroke on the substring
    // inputs (action, subject).
    const [draft, setDraft] = useState<AuditLogFilter>({});
    const [applied, setApplied] = useState<AuditLogFilter>({});
    const [cursorStack, setCursorStack] = useState<string[]>([]);
    const [currentCursor, setCurrentCursor] = useState<string | null>(null);

    const usersQuery = useUserDirectory();
    const query = useAuditLog(applied, PAGE_SIZE, currentCursor);
    const exportM = useExportAuditLog();
    const latestReview = useLatestAuditReview();

    const presets = useMemo<Preset[]>(
        () => [sinceLastReviewPreset(latestReview.data), ...STATIC_PRESETS],
        [latestReview.data],
    );

    const userOptions = useMemo(() => {
        if (!usersQuery.data) return [] as Array<{ id: string; label: string }>;
        return usersQuery.data
            .map((u) => ({ id: u.id, label: `${u.last}, ${u.first}` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [usersQuery.data]);

    function apply() {
        setApplied({ ...draft });
        setCursorStack([]);
        setCurrentCursor(null);
    }

    function clear() {
        setDraft({});
        setApplied({});
        setCursorStack([]);
        setCurrentCursor(null);
    }

    function applyPreset(preset: Preset) {
        if (preset.disabled) return;
        const next = { ...preset.build() };
        setDraft(next);
        setApplied(next);
        setCursorStack([]);
        setCurrentCursor(null);
    }

    function nextPage() {
        if (!query.data?.nextCursor) return;
        setCursorStack((s) => (currentCursor ? [...s, currentCursor] : s));
        setCurrentCursor(query.data.nextCursor);
    }

    function prevPage() {
        setCursorStack((s) => {
            if (s.length === 0) {
                setCurrentCursor(null);
                return s;
            }
            const next = s.slice(0, -1);
            setCurrentCursor(s[s.length - 1]);
            return next;
        });
    }

    async function runExport() {
        try {
            await exportM.mutateAsync(applied);
        } catch {
            // Error surface handled by the mutation state below.
        }
    }

    const page = cursorStack.length + 1;

    const rows = query.data?.entries ?? [];
    const total = query.data?.total ?? 0;
    const totalCapped = query.data?.totalCapped ?? false;
    const totalLabel = totalCapped ? `${total}+` : String(total);
    const totalPages = total === 0 ? 1 : Math.ceil(total / PAGE_SIZE);
    const totalPagesLabel = totalCapped ? `${totalPages}+` : String(totalPages);
    const rangeStart = rows.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const rangeEnd = rows.length === 0 ? 0 : (page - 1) * PAGE_SIZE + rows.length;

    return (
        <div className="p-6 max-w-[1400px] mx-auto space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-display text-2xl font-bold text-text">Audit Log</h1>
                    <p className="text-xs text-text-subtle mt-1">
                        Append-only record of every PHI access and mutation. Retained 6 years per HIPAA §164.316(b)(2).
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <MarkReviewedButton />
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={runExport}
                        disabled={exportM.isPending || query.isLoading}
                    >
                        {exportM.isPending
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Download className="w-3.5 h-3.5" />}
                        Export CSV
                    </Button>
                </div>
            </div>

            <Card>
                <CardBody>
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                        <label className="text-xs block">
                            <span className="text-text-muted">From</span>
                            <Input
                                type="date"
                                min="1900-01-01"
                                max={toIsoDate(new Date())}
                                value={draft.dateFrom ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">To</span>
                            <Input
                                type="date"
                                min={draft.dateFrom || '1900-01-01'}
                                max={toIsoDate(new Date())}
                                value={draft.dateTo ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateTo: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">User</span>
                            <select
                                value={draft.userId ?? ''}
                                onChange={(e) => setDraft({ ...draft, userId: e.target.value || undefined })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All users</option>
                                {userOptions.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Category</span>
                            <select
                                value={draft.category ?? ''}
                                onChange={(e) => setDraft({ ...draft, category: e.target.value || undefined })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Action contains</span>
                            <Input
                                type="text"
                                value={draft.action ?? ''}
                                onChange={(e) => setDraft({ ...draft, action: e.target.value || undefined })}
                                placeholder="e.g. Client viewed"
                                className="mt-1 h-8"
                            />
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Subject contains</span>
                            <Input
                                type="text"
                                value={draft.subject ?? ''}
                                onChange={(e) => setDraft({ ...draft, subject: e.target.value || undefined })}
                                placeholder="e.g. Jane Doe"
                                className="mt-1 h-8"
                            />
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Result</span>
                            <select
                                value={draft.result ?? ''}
                                onChange={(e) =>
                                    setDraft({
                                        ...draft,
                                        result: (e.target.value || undefined) as AuditResult | undefined,
                                    })
                                }
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                {RESULT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </label>
                    </div>

                    <div className="flex items-center gap-2 mt-3">
                        <Button variant="primary" size="sm" onClick={apply}>Apply</Button>
                        <Button variant="ghost" size="sm" onClick={clear}>Clear</Button>
                        {query.isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin text-text-subtle" />}
                    </div>

                    <div className="mt-4 pt-3 border-t border-border">
                        <div className="flex items-center gap-1.5 text-2xs uppercase tracking-wider text-text-subtle mb-2">
                            <Info className="w-3 h-3" />
                            Saved filters
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {presets.map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => applyPreset(p)}
                                    title={p.description}
                                    disabled={p.disabled}
                                    className="inline-flex items-center gap-1.5 px-2 py-1 text-xs bg-surface border border-border rounded-md hover:bg-surface-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-surface"
                                >
                                    <span>{p.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </CardBody>
            </Card>

            {query.isError ? (
                <Alert variant="danger">
                    Couldn't load audit log.{' '}
                    {query.error instanceof Error ? query.error.message : 'Please try again.'}
                </Alert>
            ) : null}

            <Card>
                <CardBody className="pt-0 px-0">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                <th className="px-3 py-2 font-bold w-[11rem]">Timestamp</th>
                                <th className="px-3 py-2 font-bold w-[10rem]">User</th>
                                <th className="px-3 py-2 font-bold w-[7rem]">Category</th>
                                <th className="px-3 py-2 font-bold w-[14rem]">Action</th>
                                <th className="px-3 py-2 font-bold">Subject</th>
                                <th className="px-3 py-2 font-bold w-[8rem]">Result</th>
                            </tr>
                        </thead>
                        <tbody>
                            {query.isLoading ? (
                                <tr>
                                    <td colSpan={6} className="px-3 py-6 text-center text-text-subtle">
                                        <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                                        Loading audit log…
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-3 py-8 text-center text-text-subtle text-xs">
                                        No audit entries match these filters.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((e) => (
                                    <tr key={e.id} className="border-b border-border last:border-b-0 hover:bg-surface-muted">
                                        <td className="px-3 py-2 tabular-nums text-xs text-text-subtle whitespace-nowrap">
                                            {fmtTs(e.ts)}
                                        </td>
                                        <td className="px-3 py-2 text-xs">{e.actor || '—'}</td>
                                        <td className="px-3 py-2 text-xs">{e.category}</td>
                                        <td className="px-3 py-2 text-xs">{e.action}</td>
                                        <td className="px-3 py-2 text-xs break-all">{e.subject || ''}</td>
                                        <td className={`px-3 py-2 text-xs ${resultTone(e.result)}`}>
                                            {e.result}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </CardBody>
            </Card>

            <div className="flex items-center justify-between">
                <p className="text-xs text-text-subtle">
                    Page {page} of {totalPagesLabel}
                    {rows.length > 0 && <> &middot; Rows {rangeStart}–{rangeEnd} of {totalLabel}</>}
                </p>
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={prevPage}
                        disabled={cursorStack.length === 0 && currentCursor === null}
                    >
                        <ChevronLeft className="w-3.5 h-3.5" /> Previous
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={nextPage}
                        disabled={!query.data?.nextCursor}
                    >
                        Next <ChevronRight className="w-3.5 h-3.5" />
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// Mark reviewed — dialog + POST to /api/audit-log/reviews
// ─────────────────────────────────────────────────────────────────────────

function MarkReviewedButton() {
    const [open, setOpen] = useState(false);
    const [notes, setNotes] = useState('');
    const reviewM = useCreateAuditReview();

    function openDialog() {
        setNotes('');
        reviewM.reset();
        setOpen(true);
    }

    async function submit() {
        try {
            await reviewM.mutateAsync({
                notes: notes.trim() || undefined,
            });
            setOpen(false);
        } catch {
            // Error surface rendered inside the dialog.
        }
    }

    return (
        <>
            <Button variant="ghost" size="sm" onClick={openDialog}>
                <CheckCircle2 className="w-3.5 h-3.5" /> Log a review
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="w-[min(calc(100vw-2rem),36rem)]">
                    <DialogHeader>
                        <DialogTitle>Log an audit log review</DialogTitle>
                        <DialogDescription>
                            Records a §164.308(a)(1)(ii)(D) review. Writes a single
                            "Audit log reviewed" entry into the log itself. Scope is
                            implicit — each review covers everything since the last one.
                        </DialogDescription>
                    </DialogHeader>

                    <label className="text-xs block">
                        <span className="text-text-muted">Notes <span className="text-text-subtle">(optional, ≤ 500 chars)</span></span>
                        <Textarea
                            rows={5}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            maxLength={500}
                            placeholder="e.g. No anomalies. Spot-checked 5 users. Any incident IDs, follow-ups, or coverage notes if you deviated from the monthly cadence."
                            className="mt-1"
                        />
                    </label>

                    {reviewM.isError && (
                        <Alert variant="danger" className="mt-3">
                            {extractError(reviewM.error)}
                        </Alert>
                    )}

                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm">Cancel</Button>
                        </DialogClose>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={submit}
                            disabled={reviewM.isPending}
                        >
                            {reviewM.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            Save review
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function extractError(err: unknown): string {
    if (!err) return 'Failed to save review.';
    if (typeof err === 'object' && err !== null && 'response' in err) {
        const res = (err as { response?: { data?: { error?: string } } }).response;
        if (res?.data?.error) return res.data.error;
    }
    if (err instanceof Error) return err.message;
    return 'Failed to save review.';
}
